import { IDeviceStatusItem, IDeviceCount } from '@/store/modules/metrics/types';

interface PieChartData {
  colors: string[];
  labels: string[];
  series: number[];
}

interface BarChartItem {
  name: string;
  data: number[];
}

interface BarChartData {
  series: BarChartItem[];
}

const colors: { [char: string]: string } = {
  active: '#84d681', warning: '#ec6829', locked: '#d0374d', failure: '#7b7b7b', inactive: '#333333'
};

/**
 * Return a list of standard options for pie charts
 * @param  {PieChartData} chartData, labels, series and colors for a particular pie chart
 */
function getPieChartOptions(chartData: PieChartData): Record<string, unknown> {
  const chartOptions = {
    chart: {
      type: 'donut'
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      labels: {
        colors: 'white'
      }
    },
    labels: chartData.labels,
    colors: chartData.colors,
    dataLabels: {
      enabled: false
    },
    plotOptions: {
      pie: {
        donut: {
          size: '75%',
          labels: {
            show: true,
            name: {
              show: true,
              color: 'white'
            },
            value: {
              show: true,
              color: 'white'
            },
            total: {
              show: true,
              color: 'white',
              formatter: () => chartData.series.reduce((a, b) => a + b, 0)
            }
          }
        }
      }
    }
  };
  return chartOptions;
}

/**
* Return the options for the barchart. Manage Xaxis labels from backend data
* @param  {IDeviceCount} data, data from backend to create xaxis labels
*/
function getBarChartOptions(data: IDeviceCount): Record<string, unknown> {
  const chartOptions: Record<string, any> = {
    chart: {
      toolbar: {
        show: false
      }
    },
    xaxis: {
      categories: [],
      tickAmount: 7,
      labels: {
        style: {
          colors: 'white'
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: 'white'
        }
      }
    }
  };
  data.buckets.forEach(bucket => chartOptions.xaxis.categories.push(bucket.label));
  return chartOptions;
}

/**
 * Reorganize metrics data to fit the graph series and labels
 * @param  {IDeviceStatusItem[]} data, Device status data metrics from the backend
 */
function transformDeviceStatusData(data: IDeviceStatusItem[]): PieChartData {
  const metrics: PieChartData = {
    labels: [], series: [], colors: []
  };
  const result = data.reduce((acc, curr) => {
    acc.labels.push(`dashboard.metrics.${curr.status}`);
    acc.series.push(curr.count);
    acc.colors.push(colors[curr.status]);
    return acc;
  }, metrics);
  return result;
}

/**
 * Reorganize metrics data to fit the graph series and labels
 * @param  {IDeviceCount} data, Device count data metrics from the backend
 */
function transformDeviceCountData(data: IDeviceCount): BarChartData {
  const metrics: BarChartData = {
    series: [{
      name: 'device_count',
      data: []
    }]
  };
  data.buckets.forEach(bucket => metrics.series[0].data.push(bucket.value));
  return metrics;
}

const Metrics = {
  getPieChartOptions,
  getBarChartOptions,
  transformDeviceStatusData,
  transformDeviceCountData
};

export default Metrics;
